// Detect technique aliases inside a step's instruction prose.
//
// Pure function, no React. Takes the step text + the techniques
// catalog from useTechniques(), returns a deduped, non-overlapping
// list of matches sorted by position. Powers the "Explain this
// technique →" chip row beneath each step in CookMode.
//
// Matching rules:
//
//   - Case-insensitive search of every alias against the prose.
//   - Word-boundary on BOTH ends. "browning butter" matches "brown
//     the butter" via the "brown butter" alias but NOT "brownie
//     butter". Boundary chars: anything non-alphanumeric (so a
//     trailing comma or period is fine).
//   - Longest-match wins for overlapping aliases — if both
//     "caramelize" and "caramelize onions" hit the same region,
//     the longer one wins.
//   - Deduped by slug at the chip-row layer; a single step that
//     mentions "deglaze" twice gets one chip.
//
// What this does NOT do (yet):
//
//   - Verb+object disambiguation. "Caramelize sugar" vs.
//     "caramelize onions" routes to different rows today because
//     the aliases are explicit ("caramelize the onions",
//     "caramelize the sugar") — bare "caramelize" doesn't match
//     either. When the matcher gains ingredient-context awareness,
//     bare verbs become matchable with the step's ingredient list
//     as the tiebreaker.
//   - Upgrade-vs-leave-alone. The matcher just FLAGS techniques in
//     prose — it never rewrites the step's instruction text. The
//     deterministic prose-rewriter is a separate, later concern.

const NON_WORD = /[^a-z0-9]/;

function isWordBoundary(text, idx) {
  if (idx < 0 || idx >= text.length) return true;
  return NON_WORD.test(text[idx]);
}

/**
 * @param {string} prose — the step's instruction text
 * @param {Array<{ slug, name, aliases }>} catalog — from useTechniques
 * @returns {Array<{ slug, name, matchedText, start, end }>}
 *          sorted by start, no overlaps, longest-alias-wins on conflict
 */
export function matchTechniqueInProse(prose, catalog) {
  if (typeof prose !== "string" || prose.length === 0) return [];
  if (!Array.isArray(catalog) || catalog.length === 0) return [];

  const lower = prose.toLowerCase();
  /** @type {Array<{ slug: string, name: string, start: number, end: number, length: number }>} */
  const hits = [];

  for (const tech of catalog) {
    if (!tech || !Array.isArray(tech.aliases)) continue;
    for (const aliasRaw of tech.aliases) {
      const alias = typeof aliasRaw === "string" ? aliasRaw.toLowerCase().trim() : "";
      if (alias.length === 0) continue;
      let from = 0;
      while (from <= lower.length - alias.length) {
        const idx = lower.indexOf(alias, from);
        if (idx === -1) break;
        const end = idx + alias.length;
        if (isWordBoundary(lower, idx - 1) && isWordBoundary(lower, end)) {
          hits.push({
            slug:   tech.slug,
            name:   tech.name,
            start:  idx,
            end,
            length: alias.length,
          });
        }
        from = idx + 1;
      }
    }
  }

  if (hits.length === 0) return [];

  // Longest first so the greedy pass picks them; ties on length break
  // by start position (earlier wins) for stable output.
  hits.sort((a, b) => (b.length - a.length) || (a.start - b.start));

  /** @type {Array<{ slug, name, start, end }>} */
  const accepted = [];
  for (const h of hits) {
    const overlaps = accepted.some(a => h.start < a.end && a.start < h.end);
    if (overlaps) continue;
    accepted.push({
      slug:        h.slug,
      name:        h.name,
      start:       h.start,
      end:         h.end,
      matchedText: prose.slice(h.start, h.end),
    });
  }

  accepted.sort((a, b) => a.start - b.start);
  return accepted;
}

/**
 * Convenience for the chip-row: dedupe matches by slug, preserve the
 * order of first appearance, return just { slug, name }.
 */
export function uniqueTechniquesInProse(prose, catalog) {
  const matches = matchTechniqueInProse(prose, catalog);
  const seen = new Set();
  const out = [];
  for (const m of matches) {
    if (seen.has(m.slug)) continue;
    seen.add(m.slug);
    out.push({ slug: m.slug, name: m.name });
  }
  return out;
}
